import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CalendarBlankIcon, ArrowUpRightIcon } from '@phosphor-icons/react';
import { formatDate } from '@/utils/date';
import { ConfidenceBadge, GenericInfoPanel } from '@/components';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getDetectionLabel } from '@/utils/detection-classes';
import type { Event, VehicleEvent } from '@/types/event';

interface EventDescriptionProps {
    event: Event | VehicleEvent;
    sourceName?: string;
    confidence?: number;
}

function getDisplayEvent(value: Event | VehicleEvent): Event {
    return 'event' in value && 'vehicleId' in value ? value.event : value;
}

export function EventDescription({
    event,
    sourceName,
    confidence,
}: EventDescriptionProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const actualEvent = getDisplayEvent(event);

    const descriptionText =
        actualEvent.description || getDetectionLabel(t, actualEvent.classId);
    const displayConfidence = confidence ? confidence * 100 : undefined;

    const infoItems = [
        {
            id: 'dateTime',
            label: t('infoPanel.dateTime'),
            value: formatDate(actualEvent.createdAt),
            icon: CalendarBlankIcon,
        },
        ...(sourceName
            ? [
                  {
                      id: 'source',
                      label: t('infoPanel.source'),
                      value: sourceName,
                      icon: SecurityCameraIconMirrored,
                  },
              ]
            : []),
    ];

    return (
        <div className="flex flex-col gap-6 mt-6">
            <div>
                {displayConfidence !== undefined && (
                    <ConfidenceBadge
                        className="mb-2"
                        value={displayConfidence}
                    />
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <h4 className="text-h4 text-white-100 truncate w-fit max-w-full">
                            {descriptionText}
                        </h4>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8}>
                        {descriptionText}
                    </TooltipContent>
                </Tooltip>
            </div>

            <GenericInfoPanel
                title={t('infoPanel.eventDetails')}
                className="w-full p-5"
                columns={2}
                items={infoItems}
                headerContent={
                    <Link
                        to={`/events/${actualEvent.id}`}
                        aria-label={t('infoPanel.viewEvent')}
                        className="inline-flex shrink-0 items-center text-grey-200 hover:text-grey-100 transition-colors"
                    >
                        <ArrowUpRightIcon className="size-4 shrink-0" />
                    </Link>
                }
            />
        </div>
    );
}
